<template>
  <div class="page">
    <h2>套餐订单管理</h2>
    <div class="toolbar">
      <select v-model="filters.status">
        <option value="">全部状态</option>
        <option value="PENDING_PAYMENT">待确认收款</option>
        <option value="PAID">已生效（可使用）</option>
        <option value="CANCELLED">已取消</option>
      </select>
      <button :disabled="loading" @click="loadData">查询</button>
      <router-link to="/dashboard/therapy-bundles" class="link-btn">返回套餐管理</router-link>
    </div>
    <table>
      <thead>
        <tr>
          <th>订单号</th><th>套餐</th><th>用户姓名</th><th>用户手机</th><th>金额</th><th>状态</th><th>次数</th><th>下单时间</th><th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in list" :key="item.id">
          <td>{{ item.purchaseNo }}</td>
          <td>{{ item.bundleName }}</td>
          <td>{{ item.contactName || '—' }}</td>
          <td>{{ item.contactPhone || '—' }}</td>
          <td>¥{{ item.actualAmount }}</td>
          <td>{{ item.statusLabel || statusMap[item.status] || item.status }}</td>
          <td>
            <template v-if="item.creditSummary && item.creditSummary.length">
              <span v-for="(s, idx) in item.creditSummary" :key="s.activityId">
                {{ s.activityName }}<template v-if="s.therapistGrade">({{ s.therapistGrade }})</template> 剩{{ s.available + s.reserved }}/{{ s.total }}<template v-if="idx < item.creditSummary.length - 1">；</template>
              </span>
            </template>
            <span v-else>—</span>
          </td>
          <td>{{ formatTime(item.createdAt) }}</td>
          <td>
            <button :disabled="loading" v-if="item.status === 'PENDING_PAYMENT'" @click="confirmPayment(item.id)">确认收款</button>
            <button :disabled="loading" v-if="item.status === 'PENDING_PAYMENT'" @click="cancel(item.id)">取消</button>
            <button :disabled="loading" @click="showDetail(item)">详情</button>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="detail" class="dialog-mask" @click.self="detail=null">
      <div class="dialog">
        <h3>订单详情</h3>
        <p>订单号：{{ detail.purchaseNo }}</p>
        <p>套餐：{{ detail.bundleName }}</p>
        <p>用户：{{ detail.contactName || '—' }} · {{ detail.contactPhone || '—' }}</p>
        <p>原价：¥{{ detail.originalAmount }} · 折扣：{{ detail.discountRate }}% · 实付：¥{{ detail.actualAmount }}</p>
        <p v-if="detail.expiresAt">有效期至：{{ formatTime(detail.expiresAt) }}</p>
        <div v-if="detail.itemSnapshot && detail.itemSnapshot.length">
          <strong>子项快照</strong>
          <ul>
            <li v-for="s in detail.itemSnapshot" :key="s.activityId">
              {{ s.activityName }} × {{ s.quantity }}（¥{{ s.unitPrice }}/次，小计 ¥{{ s.lineTotal }}）
            </li>
          </ul>
        </div>
        <div v-if="detail.creditSummary && detail.creditSummary.length">
          <strong>次数明细</strong>
          <ul>
            <li v-for="s in detail.creditSummary" :key="s.activityId">
              {{ s.activityName }}：可用 {{ s.available }} · 预约中 {{ s.reserved }} · 已用 {{ s.used }} · 过期 {{ s.expired }}
            </li>
          </ul>
        </div>
        <div class="actions">
          <button @click="detail=null">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { therapyBundleService } from '@/services/therapyBundleService'

export default {
  name: 'TherapyBundlePurchaseManagement',
  data() {
    return {
      list: [],
      filters: { status: 'PENDING_PAYMENT' },
      loading: false,
      detail: null,
      statusMap: {
        PENDING_PAYMENT: '待确认收款',
        PAID: '已生效（可使用）',
        CANCELLED: '已取消'
      }
    }
  },
  mounted() {
    this.applyRouteFilters()
    this.loadData()
  },
  watch: {
    '$route.query.status'() {
      this.applyRouteFilters()
      this.loadData()
    }
  },
  methods: {
    applyRouteFilters() {
      const status = this.$route.query.status
      if (status) {
        this.filters.status = status
      }
    },
    formatTime(t) {
      if (!t) return '—'
      return String(t).replace('T', ' ').slice(0, 19)
    },
    async loadData() {
      this.loading = true
      try {
        const res = await therapyBundleService.fetchPurchases({ ...this.filters, size: 100 })
        this.list = res.data?.content || []
      } catch (e) {
        alert(e?.response?.data?.error || '加载失败')
      } finally {
        this.loading = false
      }
    },
    async confirmPayment(id) {
      if (!confirm('确认已收到该套餐款项？确认后将生成可用次数。')) return
      this.loading = true
      try {
        await therapyBundleService.confirmPurchasePayment(id)
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '确认失败')
      } finally {
        this.loading = false
      }
    },
    async cancel(id) {
      if (!confirm('确定取消该订单？')) return
      this.loading = true
      try {
        await therapyBundleService.cancelPurchase(id)
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '取消失败')
      } finally {
        this.loading = false
      }
    },
    async showDetail(item) {
      this.loading = true
      try {
        const res = await therapyBundleService.fetchPurchaseDetail(item.id)
        this.detail = res.data
      } catch (e) {
        alert(e?.response?.data?.error || '加载详情失败')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.page { padding: 16px; }
.toolbar { display: flex; gap: 8px; margin-bottom: 12px; align-items: center; }
table { width: 100%; border-collapse: collapse; }
th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 13px; }
.link-btn { margin-left: auto; text-decoration: none; color: #1976d2; font-size: 14px; }
.dialog-mask { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; z-index: 100; }
.dialog { background: #fff; padding: 20px; border-radius: 8px; max-width: 520px; width: 90%; max-height: 80vh; overflow: auto; }
.actions { margin-top: 16px; text-align: right; }
</style>
